// Copyright (c) Docugami, Inc. All rights reserved.

import { IDocumentCommand } from "components/file_management/DocumentCommand";
import { AffectedItemProps, DocSetAssignment } from "components/myContentExplorer/DocSetAssignment";
import { useAppDispatch } from 'hooks/redux';
import { ClusterApi } from "networking/api";
import { useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { selectIsLimitedOrganization } from "store/AccountInfoHelpers";
import { changeClusterForDocuments, handleClusterCreated } from "store/clusters/actions";
import { useGetAffectedItemsForFilePathsMutation } from "store/frontend/frontendSlice";
import { ChangeDocSetCancelledSchema, ChangeDocSetClickedSchema, ChangeDocSetConfirmedSchema, ChangeDocSetFailedSchema, ReassignDocToExistingDocSetSchema, ReassignDocToNewDocSetSchema, RemoveDocFromDocSetSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { NavigationType, useNavigationService } from "./navigationService";

export function useAssignDocSetCommand(label: string, filePaths: string[], onComplete: (error?: any) => void, navigateOnSuccess?: boolean): IDocumentCommand {
    const [isActive, setIsActive] = useState(false);
    const disabled = useSelector((state: AppState) => selectIsLimitedOrganization(state));
    const [triggerGetAffectedItemsForFilePaths, affectedItemsResponse] = useGetAffectedItemsForFilePathsMutation();

    const navService = useNavigationService();

    const dispatch = useAppDispatch();

    async function createAndAssign(label: string): Promise<void> {
        const id = await create(label);
        if (!!id) {
            await assign(id);
        }
    }

    async function create(label: string): Promise<string> {
        try {
            const clusterApi = new ClusterApi();
            const response = await clusterApi.clusterCreateCluster(label);
            dispatch(handleClusterCreated(response.data));
            return response.data.clusterId!;
        }
        catch (error) {
            complete(error);
        }

        return '';
    }

    async function assign(id: string): Promise<void> {
        Telemetry.trackEvent<ChangeDocSetConfirmedSchema>({
            eventName: TelemetryEvent.ChangeDocSetConfirmed
        });

        await dispatch(changeClusterForDocuments(id, filePaths));
        complete();

        // Navigate the UX to the newly assigned doc set
        if (navigateOnSuccess) {
            if (!!id) {
                navService.navToDocSetPage(NavigationType.PushNewPage, id);
            } else {
                navService.navToUnassignedFiles(NavigationType.PushNewPage);
            }
        }
    }

    function onCancel() {
        Telemetry.trackEvent<ChangeDocSetCancelledSchema>({
            eventName: TelemetryEvent.ChangeDocSetCancelled
        });
        complete();
    }

    function complete(error?: any) {
        if (!!error) {
            Telemetry.trackEvent<ChangeDocSetFailedSchema>({
                error: error,
                eventName: TelemetryEvent.ChangeDocSetFailed
            });
        }

        setIsActive(false);
        onComplete(error);
    }

    async function onExecute() {
        Telemetry.trackEvent<ChangeDocSetClickedSchema>({
            eventName: TelemetryEvent.ChangeDocSetClicked
        });
        await triggerGetAffectedItemsForFilePaths({
            filePaths: filePaths
        });
        setIsActive(true);
    }

    async function onChoose(id: string | undefined) {
        if (!id) {
            Telemetry.trackEvent<RemoveDocFromDocSetSchema>(
                {
                    itemCount: filePaths.length,
                    items: filePaths,
                    eventName: TelemetryEvent.RemoveDocFromDocSet
                });
        } else {
            Telemetry.trackEvent<ReassignDocToExistingDocSetSchema>(
                {
                    docSetId: id,
                    itemCount: filePaths.length,
                    items: filePaths,
                    eventName: TelemetryEvent.ReassignDocToExistingDocSet
                });
        }

        await assign(id ?? '')
    }

    async function onCreate(name: string) {
        Telemetry.trackEvent<ReassignDocToNewDocSetSchema>(
            {
                docSetName: name,
                itemCount: filePaths.length,
                items: filePaths,
                eventName: TelemetryEvent.ReassignDocToNewDocSet
            });

        await createAndAssign(name)
    }

    function getVisual(): JSX.Element | null {
        if (!isActive || affectedItemsResponse.isLoading) {
            return null;
        }

        const deletedItems = affectedItemsResponse.data?.effects.map<AffectedItemProps>(item => ({
            name: item.name,
            type: item.itemType
        }));

        return (
            <DocSetAssignment
                deletedItems={deletedItems}
                onChoose={onChoose}
                onCreate={onCreate}
                onCancel={onCancel} />
        );
    }

    return {
        id: "Assign doc set",
        isActive: isActive,
        label: label,
        isEnabled: filePaths.length > 0 && !disabled,
        visual: getVisual(),
        execute: onExecute
    }
}
